import { c as _c } from "react/compiler-runtime";
import { useGetBulkDialogContent } from "@components/common/faro-table/bulk-dialog/bulk-dialog-helper";
import { useAppSelector } from "@store/store-helper";
import { bulkActionResultsSelector, numberOfUpdatingItemsSelector } from "@store/table/table-selector";
import { nounPluralize } from "@utils/data-display";
import { MEMBER_SEAT_LABELS } from "@utils/member-seats-utils";
import { hasSceneSeatPermission, hasXgSeatPermission } from "@utils/permission-utils";
import { getSceneLicenseBlockReason } from "@utils/scene-license-utils";
import { getXgSeatBlockReason } from "@utils/xg-seat-utils";

/** What the seats bulk action does to the selected members */

/**
 * Every change the seats bulk action offers, in the order of its menu. A Sphere XG seat can only be
 * given, never taken away again.
 */
export const SEAT_BULK_CHANGES = [{
  seat: "xgSeat",
  isAssigning: true
}, {
  seat: "sceneSeat",
  isAssigning: true
}, {
  seat: "sceneSeat",
  isAssigning: false
}];

/** Returns the label of a change, e.g. "Assign SCENE seat" */
export function getSeatBulkChangeLabel({
  seat,
  isAssigning
}) {
  return `${isAssigning ? "Assign" : "Remove"} ${MEMBER_SEAT_LABELS[seat]} seat`;
}

/**
 * Returns why the change leaves the member as they are, or undefined when it applies to them. The
 * reason is shown next to the member in the dialog, so that the user can tell why a member of their
 * selection is left out.
 */
export function getSeatBulkSkipReason(member, {
  seat,
  isAssigning
}) {
  const label = MEMBER_SEAT_LABELS[seat];
  const isHeld = seat === "xgSeat" ? hasXgSeatPermission({
    member
  }) : hasSceneSeatPermission({
    member
  });
  if (!isAssigning) {
    return isHeld ? undefined : `Has no ${label} seat to remove`;
  }
  if (isHeld) {
    return `Already has a ${label} seat`;
  }
  return seat === "xgSeat" ? getXgSeatBlockReason(member) : getSceneLicenseBlockReason(member);
}
/**
 * Returns why the selection cannot be given a seat as it is, or undefined when there are enough
 * free seats for it.
 *
 * Every member needs a seat of their own, so a selection that outnumbers the free seats would hand
 * out more seats than the plan allows. Only the members that the change actually applies to are
 * counted, since the ones that already hold a seat or cannot hold one at all need none.
 */
export function getSeatShortage({
  numberOfChangingMembers,
  seatsAvailable
}) {
  if (numberOfChangingMembers <= seatsAvailable) {
    return undefined;
  }
  return `Only ${nounPluralize({
    counter: seatsAvailable,
    word: "seat"
  })} left for ${nounPluralize({
    counter: numberOfChangingMembers,
    word: "member"
  })}. Please reduce your selection.`;
}

/** What each seat lets a member do, so that the user knows what they are handing out */
const SEAT_PURPOSES = {
  xgSeat: "so that they can take on editor and admin roles in the workspace and its projects",
  sceneSeat: "so that they can use SCENE offline and sync projects to Sphere XG"
};

/**
 * Determines the content of the bulk dialog and the bulk success dialog for giving the selected
 * members a seat or taking it away from them.
 */
export function useSeatBulkDialogContent(t0) {
  const $ = _c(25);
  const {
    seat,
    isAssigning
  } = t0;
  const numberOfUpdatingItems = useAppSelector(numberOfUpdatingItemsSelector);
  const {
    numberOfSuccess
  } = useAppSelector(bulkActionResultsSelector);
  const label = MEMBER_SEAT_LABELS[seat];
  let t1;
  if ($[0] !== numberOfUpdatingItems) {
    t1 = nounPluralize({
      counter: numberOfUpdatingItems,
      word: "this",
      shouldShowCounter: false
    });
    $[0] = numberOfUpdatingItems;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== numberOfUpdatingItems) {
    t2 = nounPluralize({
      counter: numberOfUpdatingItems,
      word: "member",
      shouldShowCounter: false
    });
    $[2] = numberOfUpdatingItems;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const theseMembers = `${t1} ${t2}`;
  let t3;
  if ($[4] !== numberOfSuccess) {
    t3 = nounPluralize({
      counter: numberOfSuccess,
      word: "member"
    });
    $[4] = numberOfSuccess;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const members = t3;
  let t4;
  if ($[6] !== isAssigning || $[7] !== seat) {
    t4 = getSeatBulkChangeLabel({
      seat,
      isAssigning
    });
    $[6] = isAssigning;
    $[7] = seat;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const t5 = `${t4}?`;
  const t6 = isAssigning ? `When you confirm this action, the following members are given a ${label} seat, ${SEAT_PURPOSES[seat]}.` : `When you confirm this action, the ${label} seats of the following members become free again and they can no longer use ${label}. They stay members of the workspace.`;
  let t7;
  if ($[9] !== t5 || $[10] !== t6) {
    t7 = {
      title: t5,
      description: t6
    };
    $[9] = t5;
    $[10] = t6;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  const t8 = isAssigning ? `Couldn't assign the ${label} seat` : `Couldn't remove the ${label} seat`;
  const t9 = isAssigning ? `The ${label} seat couldn't be assigned to ${theseMembers}, check the details and try again.` : `The ${label} seat couldn't be removed from ${theseMembers}, check the details and try again.`;
  let t10;
  if ($[12] !== t8 || $[13] !== t9) {
    t10 = {
      title: t8,
      description: t9
    };
    $[12] = t8;
    $[13] = t9;
    $[14] = t10;
  } else {
    t10 = $[14];
  }
  const t11 = isAssigning ? "assign" : "remove";
  let t12;
  if ($[15] !== t10 || $[16] !== t11 || $[17] !== t7) {
    t12 = {
      initial: t7,
      failed: t10,
      confirmButtonText: t11,
      entityType: "member"
    };
    $[15] = t10;
    $[16] = t11;
    $[17] = t7;
    $[18] = t12;
  } else {
    t12 = $[18];
  }
  const bulkDialogContent = useGetBulkDialogContent(t12);
  const t13 = isAssigning ? `${label} seat assigned` : `${label} seat removed`;
  const t14 = isAssigning ? `${members} now ${numberOfSuccess === 1 ? "holds" : "hold"} a ${label} seat.` : `${members} lost their ${label} seat, which is free again.`;
  let t15;
  if ($[19] !== t13 || $[20] !== t14) {
    t15 = {
      title: t13,
      description: t14
    };
    $[19] = t13;
    $[20] = t14;
    $[21] = t15;
  } else {
    t15 = $[21];
  }
  const successDialogContent = t15;
  let t16;
  if ($[22] !== bulkDialogContent || $[23] !== successDialogContent) {
    t16 = {
      bulkDialogContent,
      successDialogContent
    };
    $[22] = bulkDialogContent;
    $[23] = successDialogContent;
    $[24] = t16;
  } else {
    t16 = $[24];
  }
  return t16;
}